import React, { useState } from "react";
import { useSelector } from "react-redux";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import WelcomeCard from "../WelcomeCard";

const HelpUsImproveComponent = () => {
  const userId = useSelector((state) => state.auth.id);
  const [formData, setFormData] = useState({
    // Main tool feedback
    overallExperience: "",
    uiDesign: "",
    uiDesignAdditionalComments: "",
    jobImpactAssessment: "",
    aiAssessmentAdditionalComments: "",
    aiQuizAccuracy: "",
    aiQuizAdditionalComments: "",
    careerRecommendations: "",
    careerRecommendationAdditionalComments: "",
    learningPathways: "",
    learningPathwayAdditionalComments: "",
    skillsGapAnalysis: "",
    skillGapAnalysisAdditionalComments: "",
    improvementSuggestions: "",
    easeOfUnderstanding: "",
    additionalComments: "",
    recommendTool: "",

    // Chatbot specific feedback
    chatbotExperience: "",
    chatbotAccuracy: "",
    chatbotResponseTime: "",
    chatbotUsability: "",
    chatbotValue: "",
    chatbotImprovementSuggestions: "",
  });

  // Handles changes for radio buttons and text inputs
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Submits the form data
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Clean data before submitting - replace empty values with "Not Provided"
    const cleanedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        value.trim() === "" ? "Not Provided" : value.trim(),
      ])
    );

    try {
      await api1.post(API_ENDPOINTS.SAVE_FEEDBACK, {
        ...cleanedData,
        userId: userId,
      });

      // Optionally reset form
      // setFormData({...initialFormState});
    } catch (error) {
      console.error(
        "Error submitting feedback:",
        error.response?.data || error.message
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="p-6 bg-white shadow-lg rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Tool Feedback Section */}
          <div className="mb-8">
            {/* 1. Overall Experience */}
            <Question
              id="1"
              label="Overall Experience"
              sublabel="How would you rate your overall experience with the AI Job Impact Tool?"
              name="overallExperience"
              options={["Excellent", "Good", "Neutral", "Poor", "Very Poor"]}
              value={formData.overallExperience}
              onChange={handleChange}
              required={true}
            />

            {/* 2. UI & Design */}
            <Question
              id="2"
              label="User Interface & Design"
              sublabel="How would you rate the ease of use of the tool's interface and design?"
              name="uiDesign"
              options={["Excellent", "Good", "Neutral", "Poor", "Very Poor"]}
              value={formData.uiDesign}
              onChange={handleChange}
              required={true}
            />
            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the User Interface & Design"
              name="uiDesignAdditionalComments"
              value={formData.uiDesignAdditionalComments}
              onChange={handleChange}
            />

            {/* 3. Job Impact Assessment */}
            <Question
              id="3"
              label="AI Job Impact Assessment"
              sublabel="Was the AI Job Impact Assessment helpful in understanding how AI will affect your current role?"
              name="jobImpactAssessment"
              options={[
                "Very Helpful",
                "Somewhat Helpful",
                "Neutral",
                "Not Helpful",
                "Did not complete this section",
              ]}
              value={formData.jobImpactAssessment}
              onChange={handleChange}
              required={true}
            />
            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the AI Job Impact Assessment"
              name="aiAssessmentAdditionalComments"
              value={formData.aiAssessmentAdditionalComments}
              onChange={handleChange}
            />

            {/* 4. AI Literacy Assessment */}
            <Question
              id="4"
              label="AI Literacy Assessment"
              sublabel="How would you rate the AI Literacy Assessment in assessing your literacy for an AI-driven career?"
              name="aiQuizAccuracy"
              options={[
                "Very Accurate",
                "Somewhat Accurate",
                "Neutral",
                "Inaccurate",
                "Did not complete this section",
              ]}
              value={formData.aiQuizAccuracy}
              onChange={handleChange}
              required={true}
            />
            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the AI Literacy Assessment"
              name="aiQuizAdditionalComments"
              value={formData.aiQuizAdditionalComments}
              onChange={handleChange}
            />

            {/* 5. Career Recommendations */}
            <Question
              id="5"
              label="Career Recommendations"
              sublabel="How useful did you find the Career Recommendations based on your AI Job Impact results?"
              name="careerRecommendations"
              options={[
                "Very Useful",
                "Useful",
                "Neutral",
                "Not Useful",
                "Did not explore this section",
              ]}
              value={formData.careerRecommendations}
              onChange={handleChange}
              required={true}
            />

            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the Career Recommendations"
              name="careerRecommendationAdditionalComments"
              value={formData.careerRecommendationAdditionalComments}
              onChange={handleChange}
            />

            {/* 6. Learning Pathways & Resources */}
            <Question
              id="6"
              label="Learning Pathways & Resources"
              sublabel="Did you find the Learning Pathways and Resources relevant and helpful for upskilling?"
              name="learningPathways"
              options={[
                "Very Helpful",
                "Helpful",
                "Neutral",
                "Not Helpful",
                "Did not explore this section",
              ]}
              value={formData.learningPathways}
              onChange={handleChange}
              required={true}
            />

            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the Learning Pathways & Resources"
              name="learningPathwayAdditionalComments"
              value={formData.learningPathwayAdditionalComments}
              onChange={handleChange}
            />

            {/* 7. Skills Gap Analysis */}
            <Question
              id="7"
              label="Skills Gap Analysis"
              sublabel="Did you find the Skills Gap Section relevant and helpful for upskilling?"
              name="skillsGapAnalysis"
              options={[
                "Very Helpful",
                "Helpful",
                "Neutral",
                "Not Helpful",
                "Did not explore this section",
              ]}
              value={formData.skillsGapAnalysis}
              onChange={handleChange}
              required={true}
            />

            <TextInput
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the Skills Gap Analysis"
              name="skillGapAnalysisAdditionalComments"
              value={formData.skillGapAnalysisAdditionalComments}
              onChange={handleChange}
            />

            {/* 8. Suggestions for Improvement */}
            <TextInput
              id="8"
              label="Suggestions for Improvement"
              sublabel="What specific features or improvements would you like to see in the tool? (Please provide any suggestions or areas where you feel the tool can improve.)"
              name="improvementSuggestions"
              value={formData.improvementSuggestions}
              onChange={handleChange}
            />

            {/* 9. Ease of Understanding */}
            <Question
              id="9"
              label="Ease of Understanding"
              sublabel="How easy was it to understand the results of the AI Job Impact Assessment and the suggested next steps?"
              name="easeOfUnderstanding"
              options={[
                "Very Easy",
                "Easy",
                "Neutral",
                "Difficult",
                "Very Difficult",
              ]}
              value={formData.easeOfUnderstanding}
              onChange={handleChange}
              required={true}
            />

            {/* 10. Additional Comments */}
            <TextInput
              id="10"
              label="Additional Comments"
              sublabel="Please share any additional comments, thoughts, or feedback you have about the AI Job Impact Tool."
              name="additionalComments"
              value={formData.additionalComments}
              onChange={handleChange}
            />

            {/* 11. Would You Recommend This Tool? */}
            <Question
              id="11"
              label="Would You Recommend This Tool?"
              sublabel="Would you recommend the AI Job Impact Tool to a friend or colleague?"
              name="recommendTool"
              options={["Yes", "No", "Maybe"]}
              value={formData.recommendTool}
              onChange={handleChange}
              required={true}
            />
          </div>

          {/* Career Chatbot Feedback Section */}
          <div className="pt-6 border-t border-gray-200">
            <h2 className="font-bold text-2xl text-custom-pink mb-6">
              Career Chatbot Feedback Section
            </h2>

            {/* 12. Career Chatbot Experience */}
            <Question
              id="12"
              label="Career Chatbot Experience"
              sublabel="How would you rate your overall experience with the Career Chatbot?"
              name="chatbotExperience"
              options={["Excellent", "Good", "Neutral", "Poor", "Very Poor"]}
              value={formData.chatbotExperience}
              onChange={handleChange}
              required={true}
            />

            {/* 13. Chatbot Accuracy */}
            <Question
              id="13"
              label="Chatbot Accuracy"
              sublabel="Did the Career Chatbot provide accurate career recommendations and upskilling guidance?"
              name="chatbotAccuracy"
              options={[
                "Very Accurate",
                "Somewhat Accurate",
                "Neutral",
                "Inaccurate",
                "Not sure",
              ]}
              value={formData.chatbotAccuracy}
              onChange={handleChange}
              required={true}
            />

            {/* 14. Chatbot Response Time */}
            <Question
              id="14"
              label="Chatbot Response Time"
              sublabel="How would you rate the response time of the Career Chatbot?"
              name="chatbotResponseTime"
              options={["Very Fast", "Fast", "Neutral", "Slow", "Very Slow"]}
              value={formData.chatbotResponseTime}
              onChange={handleChange}
              required={true}
            />

            {/* 15. Chatbot Usability */}
            <Question
              id="15"
              label="Chatbot Usability"
              sublabel="How easy was it to interact with the Career Chatbot?"
              name="chatbotUsability"
              options={[
                "Very Easy",
                "Easy",
                "Neutral",
                "Difficult",
                "Very Difficult",
              ]}
              value={formData.chatbotUsability}
              onChange={handleChange}
              required={true}
            />

            {/* 16. Chatbot Value */}
            <Question
              id="16"
              label="Chatbot Value"
              sublabel="Did the Career Chatbot help you with your career-related queries, such as job automation, upskilling, and career recommendations?"
              name="chatbotValue"
              options={[
                "Very Helpful",
                "Helpful",
                "Neutral",
                "Not Helpful",
                "Did not use this feature",
              ]}
              value={formData.chatbotValue}
              onChange={handleChange}
              required={true}
            />

            {/* 17. Suggestions for Chatbot Improvement */}
            <TextInput
              id="17"
              label="Suggestions for Chatbot Improvement"
              sublabel="What improvements would you suggest for the Career Chatbot?"
              name="chatbotImprovementSuggestions"
              value={formData.chatbotImprovementSuggestions}
              onChange={handleChange}
            />
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              className="w-full bg-custom-gradient text-white py-3 px-4 rounded-lg transition font-medium text-lg hover:opacity-80"
            >
              Submit Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reusable Radio Button Group Component
const Question = ({
  id,
  label,
  sublabel,
  name,
  options,
  value,
  onChange,
  required = false,
}) => (
  <div className="mb-8">
    <div className="mb-2">
      <p className="font-bold text-lg text-gray-800">{`${id}. ${label}`}</p>
      <p className="text-gray-700">{sublabel}</p>
    </div>
    <div className="mt-3 flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
      {options.map((option) => (
        <label
          key={option}
          className="flex items-center space-x-2 cursor-pointer"
        >
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={onChange}
            required={required}
            className="h-4 w-4 text-blue-600"
          />
          <span className="text-sm sm:text-base">{option}</span>
        </label>
      ))}
    </div>
  </div>
);

// Reusable Text Input Component
const TextInput = ({ id, label, sublabel, name, value, onChange }) => (
  <div className="mb-8">
    <div className="mb-2">
      {id && (
        <p className="font-bold text-lg text-gray-800">{`${id}. ${label}`}</p>
      )}
      <p className="text-gray-700">{sublabel}</p>
    </div>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg p-3 mt-2 focus:border-blue-500 focus:ring focus:ring-blue-200 outline-none"
      rows="4"
      placeholder="Type your feedback here..."
    ></textarea>
  </div>
);

export default HelpUsImproveComponent;
